import React from 'react'
import { observer } from 'mobx-react-lite'

import Stack from '@mui/material/Stack'
import Tab from '@mui/material/Tab'
import Tabs from '@mui/material/Tabs'
import Tooltip from '@mui/material/Tooltip'

import {
  ANALYTICS_EVENTS,
  EmptyState,
  Loader,
  useAccountStore,
  useEvent,
  useGlobalCopyStore,
  useGlobalUiStore,
  useHoldingStore,
  useScreenSize,
  useWidgetLoadTimer,
  WidgetContainer,
} from '../common'
import { AccountType, WidgetProps } from '../core'

import ConnectionsDrawer from '../accounts/components/ConnectionsDrawer'
import Allocation from './components/Allocation'
import Analysis from './components/Analysis'
import Overview from './components/Overview'
import { TabTitle } from './components/TabTitle'

export const InvestmentsWidget = observer(({ onBackClick, onMenuClick, sx }: WidgetProps) => {
  const { onEvent } = useEvent()
  const { isMobile } = useScreenSize()
  const {
    isAccountDataLoaded,
    loadAccountData,
    visibleInvestmentAccounts: accounts,
  } = useAccountStore()
  const {
    visibleHoldings: holdings,
    hasAllocationData,
    hasAnalysisData,
    setShowConnectionsWidget,
    showConnectionsWidget,
  } = useHoldingStore()
  const { investments: copy, accounts: accountsCopy } = useGlobalCopyStore()

  const { isInitialized } = useGlobalUiStore()

  const [selectedTab, setSelectedTab] = React.useState(0)

  const { loadHoldings, holdingsLoaded } = useHoldingStore()

  useWidgetLoadTimer({
    widgetName: 'InvestmentsWidget',
    isLoaded: holdingsLoaded,
  })

  React.useEffect(() => {
    if (!isAccountDataLoaded) {
      loadAccountData().finally()
    }
  }, [])

  React.useEffect(() => {
    if (isInitialized) {
      loadHoldings().finally()
    }
  }, [isInitialized])

  // Reset to Overview if the active tab becomes disabled. Only act once holdings
  // are loaded so a mid-refetch (getters briefly false) doesn't eject the user.
  // Depend only on the boolean getters, never `visibleHoldings` (new array each
  // access → render loop).
  React.useEffect(() => {
    if (!holdingsLoaded) return
    if (selectedTab === 1 && !hasAllocationData) setSelectedTab(0)
    if (selectedTab === 2 && !hasAnalysisData) setSelectedTab(0)
  }, [selectedTab, hasAllocationData, hasAnalysisData, holdingsLoaded])

  const handleAccountFilterClick = () => {
    onEvent(ANALYTICS_EVENTS.INVESTMENT_WIDGET_ACCOUNT_FILTER_CLICK)
  }

  if (!isInitialized || !holdingsLoaded) return <Loader />

  const events = [
    ANALYTICS_EVENTS.INVESTMENT_WIDGET_DETAILS_CLICK,
    ANALYTICS_EVENTS.INVESTMENT_WIDGET_ALLOCATION_CLICK,
    ANALYTICS_EVENTS.INVESTMENT_WIDGET_ANALYSIS_CLICK,
  ]
  const isEmptyState = accounts.length === 0 && holdings.length === 0
  const investmentTabs = [
    { title: copy.tab_overview_title, disabled: false, tooltip: '' },
    {
      title: copy.tab_allocation_title,
      disabled: !hasAllocationData,
      tooltip: copy.allocation.empty_data,
    },
    {
      title: copy.tab_analysis_title,
      disabled: !hasAnalysisData,
      tooltip: copy.analysis.empty_data,
    },
  ]
  return (
    <WidgetContainer
      allowedAccountTypes={[AccountType.INVESTMENT]}
      onAccountsFilterClick={handleAccountFilterClick}
      onBackClick={onBackClick}
      onMenuClick={onMenuClick}
      sx={sx}
      title={copy.title}
    >
      <Stack mx={isMobile ? 0 : 48} my={24}>
        <Tabs
          onChange={(_, newValue) => {
            // aria-disabled tabs stay clickable; block their selection here.
            if (isEmptyState || investmentTabs[newValue]?.disabled) return
            setSelectedTab(newValue)
            onEvent(events[newValue])
          }}
          sx={{ bgcolor: 'background.paper' }}
          textColor="primary"
          value={!isEmptyState ? selectedTab : false}
          variant="fullWidth"
        >
          {investmentTabs.map(({ title, disabled, tooltip }) => {
            const isDisabled = isEmptyState || disabled
            const isTooltipTab = isDisabled && !isEmptyState && Boolean(tooltip)
            // aria-disabled (not native disabled) keeps the Tab focusable and in
            // the a11y tree; describeChild adds the reason without stealing the name.
            const tab = (
              <Tab
                aria-disabled={isDisabled || undefined}
                key={title}
                label={<TabTitle title={title} />}
                sx={{
                  ...(isTooltipTab && { flex: 1 }),
                  ...(isDisabled && {
                    opacity: 0.38,
                    cursor: 'default',
                  }),
                }}
              />
            )

            return isTooltipTab ? (
              <Tooltip describeChild key={title} title={tooltip}>
                {tab}
              </Tooltip>
            ) : (
              tab
            )
          })}
        </Tabs>
        {isEmptyState ? (
          <EmptyState
            header={copy.empty_state_header}
            icon="bid_landscape"
            onClick={() => {
              setShowConnectionsWidget(true)
              onEvent(ANALYTICS_EVENTS.ACCOUNTS_CLICK_CONNECT)
            }}
            primaryButton={copy.overview.connect_accounts}
            subText={copy.empty_state_description}
            sx={{
              width: { sm: 432 },
              '.buttons-footer': {
                width: 167,
              },
              '.MuiTypography-h2': {
                mt: 20,
                mb: 4,
              },
              px: 16,
            }}
          />
        ) : (
          <>
            {selectedTab === 0 && <Overview />}
            {selectedTab === 1 && <Allocation />}
            {selectedTab === 2 && <Analysis />}
          </>
        )}
      </Stack>
      <ConnectionsDrawer
        onClose={() => setShowConnectionsWidget(false)}
        showConnectionsWidget={showConnectionsWidget}
        title={accountsCopy.manage_connections}
      />
    </WidgetContainer>
  )
})
